import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' },
  { id: 'saturday', label: 'Saturday' },
  { id: 'sunday', label: 'Sunday' },
];

const ProviderAvailability = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [acceptBookings, setAcceptBookings] = useState(true);
  const [workingHours, setWorkingHours] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState({ date: '', reason: '' });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Get auth token from localStorage
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/provider/availability`, {
        headers: getAuthHeader()
      });
      const data = response.data;
      setAcceptBookings(data.accept_bookings !== false);
      setWorkingHours(data.working_hours || []);
      setBlockedDates(data.blocked_dates || []);
    } catch (error) {
      console.error('Error fetching availability:', error);
      // Set defaults
      setWorkingHours(DAYS_OF_WEEK.map(day => ({
        day: day.id,
        is_working: !['saturday', 'sunday'].includes(day.id),
        start_time: '09:00',
        end_time: '17:00'
      })));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAcceptBookings = async () => {
    try {
      const newValue = !acceptBookings;
      await axios.put(`${API}/provider/availability/toggle?accept=${newValue}`, {}, {
        headers: getAuthHeader()
      });
      setAcceptBookings(newValue);
      showMessage('success', `Booking acceptance ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      showMessage('error', 'Failed to update setting');
    }
  };

  const handleWorkingHoursChange = (dayId, field, value) => {
    setWorkingHours(prev => {
      // Check if the day already exists in the array
      const existingIndex = prev.findIndex(wh => wh.day === dayId);
      
      if (existingIndex >= 0) {
        // Update existing entry
        return prev.map(wh => 
          wh.day === dayId ? { ...wh, [field]: value } : wh
        );
      } else {
        // Add new entry for this day
        const newEntry = {
          day: dayId,
          is_working: field === 'is_working' ? value : false,
          start_time: field === 'start_time' ? value : '09:00',
          end_time: field === 'end_time' ? value : '17:00'
        };
        return [...prev, newEntry];
      }
    });
  };

  const handleSaveWorkingHours = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/provider/availability/working-hours`, {
        working_hours: workingHours
      }, {
        headers: getAuthHeader()
      });
      showMessage('success', 'Working hours saved successfully');
    } catch (error) {
      showMessage('error', 'Failed to save working hours');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlockedDate = async () => {
    if (!newBlockedDate.date) {
      showMessage('error', 'Please select a date');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/provider/availability/blocked-dates`, newBlockedDate, {
        headers: getAuthHeader()
      });
      setBlockedDates([...blockedDates, response.data.blocked_date]);
      setNewBlockedDate({ date: '', reason: '' });
      showMessage('success', 'Date blocked successfully');
    } catch (error) {
      showMessage('error', error.response?.data?.detail || 'Failed to block date');
    }
  };

  const handleRemoveBlockedDate = async (dateId) => {
    try {
      await axios.delete(`${API}/provider/availability/blocked-dates/${dateId}`, {
        headers: getAuthHeader()
      });
      setBlockedDates(blockedDates.filter(d => d.id !== dateId));
      showMessage('success', 'Blocked date removed');
    } catch (error) {
      showMessage('error', 'Failed to remove blocked date');
    }
  };

  const showMessage = (type, msg) => {
    if (type === 'success') {
      setSuccess(msg);
      setError('');
    } else {
      setError(msg);
      setSuccess('');
    }
    setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3000);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-PH', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get minimum date for blocking (today)
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg" data-testid="provider-availability">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-gradient-to-r from-orange-500 to-orange-600 rounded-t-lg flex justify-between items-center">
        <h2 className="text-xl font-bold text-white">Manage Availability</h2>
        {onClose && (
          <button onClick={onClose} className="text-white hover:opacity-80">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="p-6 max-h-[70vh] overflow-y-auto">
        {/* Messages */}
        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            ✓ {success}
          </div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Accept Bookings Toggle */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-800">Accept New Bookings</h3>
              <p className="text-sm text-gray-600">Toggle off to temporarily stop receiving booking requests</p>
            </div>
            <button
              onClick={handleToggleAcceptBookings}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                acceptBookings ? 'bg-green-500' : 'bg-gray-300'
              }`}
              data-testid="accept-bookings-toggle"
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform ${
                  acceptBookings ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Working Hours */}
        <div className="mb-6">
          <h3 className="font-bold text-gray-800 mb-4">Working Hours</h3>
          <div className="space-y-3">
            {DAYS_OF_WEEK.map((day) => {
              const dayHours = workingHours.find(wh => wh.day === day.id) || {
                day: day.id,
                is_working: false,
                start_time: '09:00',
                end_time: '17:00'
              };
              
              return (
                <div
                  key={day.id}
                  className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50"
                  data-testid={`working-hours-${day.id}`}
                >
                  <div className="w-28">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={dayHours.is_working}
                        onChange={(e) => handleWorkingHoursChange(day.id, 'is_working', e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
                      />
                      <span className={`font-medium ${dayHours.is_working ? 'text-gray-800' : 'text-gray-400'}`}>
                        {day.label}
                      </span>
                    </label>
                  </div>
                  
                  {dayHours.is_working ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input
                        type="time"
                        value={dayHours.start_time}
                        onChange={(e) => handleWorkingHoursChange(day.id, 'start_time', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white cursor-pointer hover:border-orange-400"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="time"
                        value={dayHours.end_time}
                        onChange={(e) => handleWorkingHoursChange(day.id, 'end_time', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white cursor-pointer hover:border-orange-400"
                      />
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm italic">Closed</span>
                  )}
                </div>
              );
            })}
          </div>
          
          <button
            onClick={handleSaveWorkingHours}
            disabled={saving}
            className="mt-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
            data-testid="save-working-hours-btn"
          >
            {saving ? 'Saving...' : 'Save Working Hours'}
          </button>
        </div>

        {/* Blocked Dates */}
        <div>
          <h3 className="font-bold text-gray-800 mb-4">Blocked Dates (Vacations, Personal Days)</h3>
          
          {/* Add New Blocked Date */}
          <div className="flex flex-wrap gap-3 mb-4 p-4 bg-gray-50 rounded-lg">
            <input
              type="date"
              value={newBlockedDate.date}
              onChange={(e) => setNewBlockedDate({ ...newBlockedDate, date: e.target.value })}
              min={today}
              className="px-3 py-2 border rounded-lg"
              data-testid="blocked-date-input"
            />
            <input
              type="text"
              value={newBlockedDate.reason}
              onChange={(e) => setNewBlockedDate({ ...newBlockedDate, reason: e.target.value })}
              placeholder="Reason (optional)"
              className="flex-1 min-w-[150px] px-3 py-2 border rounded-lg"
              data-testid="blocked-reason-input"
            />
            <button
              onClick={handleAddBlockedDate}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700"
              data-testid="add-blocked-date-btn"
            >
              + Block Date
            </button>
          </div>

          {/* Blocked Dates List */}
          {blockedDates.length === 0 ? (
            <p className="text-gray-500 text-sm">No dates blocked. Add dates when you&apos;re unavailable.</p>
          ) : (
            <div className="space-y-2">
              {blockedDates.map((blocked) => (
                <div
                  key={blocked.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                  data-testid={`blocked-date-${blocked.id}`}
                >
                  <div>
                    <span className="font-medium">{formatDate(blocked.date)}</span>
                    {blocked.reason && (
                      <span className="text-gray-500 text-sm ml-2">- {blocked.reason}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveBlockedDate(blocked.id)}
                    className="text-red-500 hover:text-red-700"
                    data-testid={`remove-blocked-${blocked.id}`}
                  >
                    🗑️ Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProviderAvailability;
